import type { ClientMessage, ServerMessage } from "@vinyl/shared";

type MessageListener = (msg: ServerMessage) => void;
type ConnectionListener = (open: boolean) => void;

export class GameClient {
  private ws: WebSocket | null = null;
  private readonly messageListeners = new Set<MessageListener>();
  private readonly connectionListeners = new Set<ConnectionListener>();
  private reconnectDelay = 1000;
  private reconnectTimer: number | null = null;
  private wantConnected = false;

  constructor(private readonly url: string) {}

  connect(): void {
    this.wantConnected = true;
    this.openSocket();
  }

  disconnect(): void {
    this.wantConnected = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }

  send(msg: ClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  onMessage(fn: MessageListener): () => void {
    this.messageListeners.add(fn);
    return () => this.messageListeners.delete(fn);
  }

  onConnection(fn: ConnectionListener): () => void {
    this.connectionListeners.add(fn);
    return () => this.connectionListeners.delete(fn);
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private openSocket(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectDelay = 1000;
      for (const l of this.connectionListeners) l(true);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(String(event.data)) as ServerMessage;
        for (const l of this.messageListeners) l(msg);
      } catch (err) {
        console.error("[client] bad server message:", err);
      }
    };

    ws.onclose = () => {
      for (const l of this.connectionListeners) l(false);
      if (this.wantConnected) this.scheduleReconnect();
    };

    ws.onerror = () => {
      // Errors are followed by close; nothing to do here.
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(delay * 2, 15_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wantConnected) this.openSocket();
    }, delay);
  }
}
